import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { isSaudiBrand } from './products/saudi/utils/isSaudiBrand';
import { SAUDI_BRAND } from './products/saudi/config/saudiBrandConfig';

// Qawaem ERP - Arabic only
import translationAR from './locales/ar/translation.json';
import achatsPagesAR from './locales/ar/achatsPages.json';
import ventesPagesAR from './locales/ar/ventesPages.json';
import facturationPagesAR from './locales/ar/facturationPages.json';
import articlePagesAR from './locales/ar/articlePages.json';
import crmPagesAR from './locales/ar/crmPages.json';
import comptabilitePagesAR from './locales/ar/comptabilitePages.json';
import caissePagesAR from './locales/ar/caissePages.json';
import tresoreriePagesAR from './locales/ar/tresoreriePages.json';
import banquePagesAR from './locales/ar/banquePages.json';
import salairesPagesAR from './locales/ar/salairesPages.json';
import pointagePagesAR from './locales/ar/pointagePages.json';
import hrPagesAR from './locales/ar/hrPages.json';
import employeePortalPagesAR from './locales/ar/employeePortalPages.json';
import settingsPagesAR from './locales/ar/settingsPages.json';
import inventairePagesAR from './locales/ar/inventairePages.json';
import approbationsPagesAR from './locales/ar/approbationsPages.json';
import documentCreationFormAR from './locales/ar/documentCreationForm.json';
import aiAgentPagesAR from './locales/ar/aiAgentPages.json';

const { shared: employeePortalSharedAR, ...employeePortalPagesOnlyAR } = employeePortalPagesAR;

/** Deep-merge page namespaces so translation.json base keys are preserved. */
function mergeModulePages(basePages, overridePages) {
  const merged = { ...(basePages || {}) };
  for (const [pageKey, pageKeys] of Object.entries(overridePages || {})) {
    merged[pageKey] = { ...(merged[pageKey] || {}), ...pageKeys };
  }
  return merged;
}

const resources = {
  ar: {
    translation: {
      ...translationAR,
      document_creation: {
        ...(translationAR.document_creation || {}),
        form: {
          ...(translationAR.document_creation?.form || {}),
          ...documentCreationFormAR,
        },
      },
      modules: {
        ...(translationAR.modules || {}),
        achats: {
          ...(translationAR.modules?.achats || {}),
          pages: achatsPagesAR,
        },
        ventes: {
          ...(translationAR.modules?.ventes || {}),
          pages: mergeModulePages(translationAR.modules?.ventes?.pages, ventesPagesAR),
        },
        facturation: {
          ...(translationAR.modules?.facturation || {}),
          pages: mergeModulePages(translationAR.modules?.facturation?.pages, facturationPagesAR),
        },
        crm: {
          ...(translationAR.modules?.crm || {}),
          pages: mergeModulePages(translationAR.modules?.crm?.pages, crmPagesAR),
        },
        article: {
          ...(translationAR.modules?.article || {}),
          pages: mergeModulePages(translationAR.modules?.article?.pages, articlePagesAR),
        },
        inventaire: {
          ...(translationAR.modules?.inventaire || {}),
          pages: mergeModulePages(translationAR.modules?.inventaire?.pages, inventairePagesAR),
        },
        comptabilite: {
          ...(translationAR.modules?.comptabilite || {}),
          pages: mergeModulePages(translationAR.modules?.comptabilite?.pages, comptabilitePagesAR),
        },
        caisse: {
          ...(translationAR.modules?.caisse || {}),
          pages: mergeModulePages(translationAR.modules?.caisse?.pages, caissePagesAR),
        },
        tresorerie: {
          ...(translationAR.modules?.tresorerie || {}),
          pages: mergeModulePages(translationAR.modules?.tresorerie?.pages, tresoreriePagesAR),
        },
        banque: {
          ...(translationAR.modules?.banque || {}),
          pages: mergeModulePages(translationAR.modules?.banque?.pages, banquePagesAR),
        },
        salaires: {
          ...(translationAR.modules?.salaires || {}),
          pages: mergeModulePages(translationAR.modules?.salaires?.pages, salairesPagesAR),
        },
        pointage: {
          ...(translationAR.modules?.pointage || {}),
          timeTrackingCodes: pointagePagesAR.timeTrackingCodes || {},
          pages: mergeModulePages(
            translationAR.modules?.pointage?.pages,
            Object.fromEntries(
              Object.entries(pointagePagesAR).filter(([key]) => key !== 'timeTrackingCodes')
            )
          ),
        },
        ressources_humaines: {
          ...(translationAR.modules?.ressources_humaines || {}),
          pages: mergeModulePages(translationAR.modules?.ressources_humaines?.pages, hrPagesAR),
        },
        employee_portal: {
          ...(translationAR.modules?.employee_portal || {}),
          pages: mergeModulePages(translationAR.modules?.employee_portal?.pages, employeePortalPagesOnlyAR),
          shared: {
            ...(translationAR.modules?.employee_portal?.shared || {}),
            ...(employeePortalSharedAR || {}),
          },
        },
        approbations: {
          ...(translationAR.modules?.approbations || {}),
          pages: mergeModulePages(translationAR.modules?.approbations?.pages, approbationsPagesAR),
        },
        shared: {
          ...(translationAR.modules?.shared || {}),
          ...(settingsPagesAR.shared || {}),
          aiAgent: aiAgentPagesAR,
          settingsBody: {
            ...(translationAR.modules?.shared?.settingsBody || {}),
            ...(settingsPagesAR.shared?.settingsBody || {}),
          },
          profile: {
            ...(translationAR.modules?.shared?.profile || {}),
            ...(settingsPagesAR.shared?.profile || {}),
          },
        },
      },
    },
  },
};

const defaultLng = 'ar';
const saudiBrand = isSaudiBrand();

if (saudiBrand) {
  try {
    localStorage.setItem('language', defaultLng);
  } catch {
    // ignore storage errors
  }
}

const i18nChain = i18n.use(initReactI18next);

i18nChain.init({
  resources,
  lng: defaultLng,
  fallbackLng: 'ar',
  supportedLngs: ['ar'],
  nonExplicitSupportedLngs: false,
  load: 'languageOnly',
  returnObjects: false,
  returnEmptyString: false,
  returnNull: false,
  parseMissingKeyHandler: (key, defaultValue) => defaultValue || key,
  debug: false,
  interpolation: {
    escapeValue: false,
  },
  react: {
    useSuspense: false,
  },
});

try {
  i18n.changeLanguage(defaultLng);
} catch (error) {
  console.error('Error loading language preference:', error);
  i18n.changeLanguage(defaultLng);
}

const applyDocumentDirection = (lng) => {
  document.documentElement.dir = 'rtl';
  document.documentElement.lang = 'ar';
};

applyDocumentDirection(i18n.language);
i18n.on('languageChanged', (lng) => {
  applyDocumentDirection(lng);
});

export default i18n;
